import { useState, useRef } from 'react';
import { Send, Paperclip, X, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface MessageInputProps {
  onSend: (message: string, file?: File, isInternal?: boolean) => Promise<void>;
  showInternalToggle?: boolean;
  placeholder?: string;
}

export function MessageInput({ onSend, showInternalToggle = false, placeholder = 'Type a message...' }: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleSend = async () => {
    if (!message.trim() && !file) return;
    setSending(true);
    try {
      await onSend(message.trim(), file || undefined, isInternal);
      setMessage('');
      setFile(null);
      setIsInternal(false);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (selected.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }

    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowed.includes(selected.type)) {
      toast.error('Only images and PDFs are allowed');
      return;
    }

    setFile(selected);
  };

  return (
    <div className="border-t border-border p-4">
      {file && (
        <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-muted/30 border border-border w-fit">
          <Paperclip className="w-3 h-3 text-muted-foreground" />
          <span className="text-sm truncate max-w-[200px]">{file.name}</span>
          <button onClick={() => setFile(null)} className="text-muted-foreground hover:text-foreground">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
      <div className="flex items-center gap-2">
        {showInternalToggle && (
          <Button
            variant={isInternal ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setIsInternal(!isInternal)}
            className={`h-9 px-2 ${isInternal ? 'bg-amber-500/20 text-amber-500 hover:bg-amber-500/30' : ''}`}
            title={isInternal ? 'Internal note (hidden from client)' : 'Click to make internal note'}
          >
            <Lock className="w-4 h-4" />
          </Button>
        )}
        <input ref={fileRef} type="file" className="hidden" onChange={handleFileSelect} accept="image/*,.pdf" />
        <Button variant="ghost" size="sm" onClick={() => fileRef.current?.click()} className="h-9 px-2">
          <Paperclip className="w-4 h-4" />
        </Button>
        <input
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder={isInternal ? 'Internal note (hidden from client)...' : placeholder}
          className="flex-1 bg-transparent border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <Button onClick={handleSend} disabled={sending || (!message.trim() && !file)} size="sm" className="h-9 px-3">
          <Send className="w-4 h-4" />
        </Button>
      </div>
      {isInternal && (
        <p className="text-xs text-amber-500 mt-1 ml-10">This note is only visible to admins</p>
      )}
    </div>
  );
}
